/**
 * `CellStoreAddress` — where a single local cell edit lands, as a **value**
 *.
 *
 * `SheetDataSource.applyCellEditLocal` is the SDK's row-keyed edit chokepoint.
 * It used to *interleave* the decision with the dispatch: an `isCanonicalKey`
 * test, a `storeWritten` flag threaded through two nested loops, and a
 * `!storeWritten` fallback at the bottom — so "which store owns this write"
 * could only be read by executing it. Nothing could test the routing without a
 * live client, and nothing could report the routing at all.
 *
 * The address is now chosen ONCE, by the pure `resolveCellStoreAddress`, and
 * then dispatched. The resolver takes a description of the world (is the
 * canonical store attached? which open sheets carry a matching row? is a handle
 * resolver wired?) and returns the ordered plan; the caller only applies it.
 *
 * ## The three addresses, and why the order is part of the contract
 *
 * 1. `canonical` — the client-shared rows-side store, keyed by the `:~` lookup
 *    key. One write, works with zero open sheets.
 * 2. `sheet-local` — one open sheet's `sourceCellData`, keyed by the interned
 *    `RowLocalId`. One per matching (sheet, row) pair.
 * 3. `handle` — the backend-registry `RowCellCache` for the cell-access key. The
 *    FALLBACK: taken only when neither store above claimed the write, because
 *    the store→handle feed (`CellStoreView.link`) already delivers a store
 *    write to the handle, and writing both would double-fire every listener.
 *
 * `Plan.rows` is ordered, and each entry's store write happens BEFORE its
 * `RowDataChanged`. That interleaving is observable — `InvalidationSink.emit`
 * dispatches synchronously, so a subscriber woken by sheet N's notification can
 * read sheet N+1's store and must see it un-written. Preserving it is what lets
 * this extraction claim byte-identical behavior.
 */

import type { Option } from "@tento-lona/sheets";
import { RowId, RowNetworkKey } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";

import type { SheetId } from "@tento-lona/sheets";
import type { CellCardinality } from "@tento-lona/sheets/platform";

/**
 * Which store holds a cell: the client's canonical store, a sheet's local
 * store, or a handle's.
 */
export type CellStoreAddress =
  | CellStoreAddress.Canonical
  | CellStoreAddress.SheetLocal
  | CellStoreAddress.Handle;

/**
 * Types and values that go with `CellStoreAddress`: `Canonical`, `SheetLocal`,
 * `Handle`, `Match`, `Input`, `RowStep`, and 2 more.
 */
export namespace CellStoreAddress {
  /**
   * The client-shared rows-side canonical store. Keyed by the `:~` lookup key
   * itself (that store is `CellDataStore<string>`, not row-id keyed), and
   * carrying the cardinality the store needs before its FIRST write — a
   * multi-card store keys cells by id.
   */
  export interface Canonical {
    readonly kind: "canonical";
    readonly canonicalKey: string;
    readonly cardinality: CellCardinality;
  }

  /** One open sheet's `sourceCellData`, keyed by the interned row id. */
  export interface SheetLocal {
    readonly kind: "sheet-local";
    readonly sheetId: SheetId;
    readonly rowId: RowLocalId;
  }

  /**
   * The backend-registry `RowCellCache` for the cell-access key. Reached only when
   * no store claimed the write (no rows accessor, or no open sheet carries the
   * row) — otherwise the store→handle bridge already mirrors it.
   */
  export interface Handle {
    readonly kind: "handle";
    readonly accessKey: RowId;
  }

  /** One open sheet's row that matched the edited key, in `sheetHandles` order. */
  export interface Match {
    readonly sheetId: SheetId;
    readonly rowId: RowLocalId;
    /** Whether that sheet carries a rows-side sheet able to take the write. */
    readonly hasSheetStore: boolean;
  }

  /** Everything the routing decision depends on, and nothing else. */
  export interface Input {
    /** The edited row's key — a row id or a lookup key. */
    readonly rowKey: RowNetworkKey;
    /** True when `rowKey` is a canonical (`:~…`) lookup key. */
    readonly isCanonical: boolean;
    /** Whether the client-shared canonical store has been attached yet. */
    readonly canonicalStoreAttached: boolean;
    /**
     * The cardinality an open sheet's rows-side descriptor declares for this
     * key, when one is open. `null` ⇒ infer from the write itself.
     */
    readonly declaredCardinality: Option<CellCardinality>;
    /**
     * Whether the write carries a cell id. An id-keyed upsert IS the multi-card
     * contract (tasks, events), so it is the inference used when no descriptor
     * answers.
     */
    readonly hasCellId: boolean;
    /** Open sheets carrying a row matching `rowKey`, in `sheetHandles` order. */
    readonly matches: readonly Match[];
    /** Whether a backend-registry handle resolver is wired on the data source. */
    readonly handleResolverAttached: boolean;
  }

  /** One matched row's share of the plan: its store write, then its notify. */
  export interface RowStep {
    /** The sheet-local write, or `null` when this match only notifies. */
    readonly store: Option<SheetLocal>;
    /** The row to raise `RowDataChanged` for — every match notifies. */
    readonly notify: RowLocalId;
  }

  /** The resolved routing, in dispatch order. */
  export interface Plan {
    /** The canonical store write. Applied first, before any row step. */
    readonly canonical: Option<Canonical>;
    /**
     * Per matched row, in order: write (when present) then notify.
     * INDEX-ALIGNED with `Input.matches` — one entry each, same order — so a
     * caller may carry its own parallel array (the live store objects, say)
     * without re-deriving anything from the addresses.
     */
    readonly rows: readonly RowStep[];
    /** The handle fallback. Applied last, and only when nothing above wrote. */
    readonly fallback: Option<Handle>;
  }

  /** Every store address the plan writes to — the routing, without the notifies. */
  export function writes(plan: Plan): readonly CellStoreAddress[] {
    const out: CellStoreAddress[] = [];
    if (plan.canonical != null) out.push(plan.canonical);
    for (const row of plan.rows) {
      if (row.store != null) out.push(row.store);
    }
    if (plan.fallback != null) out.push(plan.fallback);
    return out;
  }
}

/**
 * Choose the address(es) for one local cell edit. Pure: no stores, no sink, no
 * DOM, no `this` — the caller resolves `Input` from live state and applies the
 * returned `Plan`. This is the whole routing decision.
 */
export function resolveCellStoreAddress(
  input: CellStoreAddress.Input,
): CellStoreAddress.Plan {
  const canonicalKey = input.isCanonical ? input.rowKey : null;

  // 1. The canonical store, when the key is canonical AND the store is attached.
  //    Cell cardinality: the rows-side descriptor wins; otherwise an id-keyed write
  //    is the multi-card contract and an id-less one is single.
  const canonical: Option<CellStoreAddress.Canonical> =
    canonicalKey != null && input.canonicalStoreAttached
      ? {
        kind: "canonical",
        canonicalKey,
        cardinality: input.declaredCardinality ??
          (input.hasCellId ? "multi" : "single"),
      }
      : null;

  // 2. Per matched row. A canonical key never writes a sheet-local store — its
  //    home is the shared canonical one, and the per-sheet fan-out is the
  //    canonical store's own subscriber.
  const rows: CellStoreAddress.RowStep[] = [];
  for (const match of input.matches) {
    const takesWrite = canonicalKey == null && match.hasSheetStore;
    rows.push({
      store: takesWrite
        ? { kind: "sheet-local", sheetId: match.sheetId, rowId: match.rowId }
        : null,
      notify: match.rowId,
    });
  }

  // 3. The fallback — only when NO store above claimed the write.
  const storeWritten = canonical != null ||
    rows.some((row) => row.store != null);
  const fallback: Option<CellStoreAddress.Handle> =
    !storeWritten && input.handleResolverAttached
      ? { kind: "handle", accessKey: RowId.of(canonicalKey ?? input.rowKey) }
      : null;

  return { canonical, rows, fallback };
}
